import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { mockProducts, type Category, type Grade, type Product } from './mockProducts';
import { CatalogueFilters } from './CatalogueFilters';
import { ProductList } from './ProductList';
import styles from './CatalogueDemo.module.css';

export function CatalogueDemo() {
  const { t } = useTranslation('catalogue');

  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [activeGrades, setActiveGrades] = useState<Set<Grade>>(new Set());
  const [activeYears, setActiveYears] = useState<Set<number>>(new Set());
  const [activeColors, setActiveColors] = useState<Set<string>>(new Set());
  const [activeBrands, setActiveBrands] = useState<Set<string>>(new Set());
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1500]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [isVisible, setIsVisible] = useState(false);
  const [hintVisible, setHintVisible] = useState(true);
  const hasAnimated = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const categoryCounts = useMemo(() => {
    const counts: Record<Category, number> = { all: mockProducts.length, smartphones: 0, tablettes: 0, laptops: 0 };
    for (const p of mockProducts) counts[p.category]++;
    return counts;
  }, []);

  const categoryProducts = useMemo(() => {
    const seededShuffle = (arr: Product[]) => {
      const copy = [...arr];
      let seed = 42;
      for (let i = copy.length - 1; i > 0; i--) {
        seed = (seed * 16807 + 0) % 2147483647;
        const j = seed % (i + 1);
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    };
    if (activeCategory !== 'all') return seededShuffle(mockProducts.filter(p => p.category === activeCategory));
    const groups: Record<string, Product[]> = { smartphones: [], tablettes: [], laptops: [] };
    for (const p of mockProducts) groups[p.category].push(p);
    for (const cat of Object.keys(groups)) groups[cat] = seededShuffle(groups[cat]);
    const interleaved: Product[] = [];
    const maxLen = Math.max(...Object.values(groups).map(g => g.length));
    for (let i = 0; i < maxLen; i++) {
      for (const cat of ['smartphones', 'tablettes', 'laptops'] as const) {
        if (i < groups[cat].length) interleaved.push(groups[cat][i]);
      }
    }
    return interleaved;
  }, [activeCategory]);

  const years = useMemo(() => {
    const set = new Set<number>();
    for (const p of categoryProducts) set.add(p.year);
    return Array.from(set).sort((a, b) => b - a);
  }, [categoryProducts]);

  const colors = useMemo(() => {
    const set = new Set<string>();
    for (const p of categoryProducts) set.add(p.color);
    return Array.from(set).sort();
  }, [categoryProducts]);

  const brands = useMemo(() => {
    const set = new Set<string>();
    for (const p of categoryProducts) set.add(p.brand);
    return Array.from(set).sort();
  }, [categoryProducts]);

  const priceBounds = useMemo(() => {
    if (categoryProducts.length === 0) return { min: 0, max: 1500 };
    let lo = Infinity, hi = -Infinity;
    for (const p of categoryProducts) {
      if (p.priceHT < lo) lo = p.priceHT;
      if (p.priceHT > hi) hi = p.priceHT;
    }
    return { min: Math.floor(lo / 10) * 10, max: Math.ceil(hi / 10) * 10 };
  }, [categoryProducts]);

  const categoryPrices = useMemo(
    () => categoryProducts.map(p => p.priceHT),
    [categoryProducts]
  );

  useEffect(() => {
    setActiveBrands(new Set());
    setActiveColors(new Set());
    setActiveYears(new Set());
    setActiveGrades(new Set());
    setPriceRange([priceBounds.min, priceBounds.max]);
    setSearch('');
    setSelected(new Set());
  }, [activeCategory, priceBounds.min, priceBounds.max]);

  const filteredProducts = useMemo(() => {
    let list = categoryProducts;
    if (activeGrades.size > 0) {
      list = list.filter(p => activeGrades.has(p.grade));
    }
    if (activeYears.size > 0) {
      list = list.filter(p => activeYears.has(p.year));
    }
    if (activeColors.size > 0) {
      list = list.filter(p => activeColors.has(p.color));
    }
    if (activeBrands.size > 0) {
      list = list.filter(p => activeBrands.has(p.brand));
    }
    if (priceRange[0] > priceBounds.min || priceRange[1] < priceBounds.max) {
      list = list.filter(p => p.priceHT >= priceRange[0] && p.priceHT <= priceRange[1]);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.model.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q)
      );
    }
    return list;
  }, [categoryProducts, activeGrades, activeYears, activeColors, activeBrands, priceRange, priceBounds, search]);

  const handleGradeToggle = useCallback((grade: Grade) => {
    setActiveGrades(prev => {
      const next = new Set(prev);
      next.has(grade) ? next.delete(grade) : next.add(grade);
      return next;
    });
  }, []);

  const handleYearToggle = useCallback((year: number) => {
    setActiveYears(prev => {
      const next = new Set(prev);
      next.has(year) ? next.delete(year) : next.add(year);
      return next;
    });
  }, []);

  const handleColorToggle = useCallback((color: string) => {
    setActiveColors(prev => {
      const next = new Set(prev);
      next.has(color) ? next.delete(color) : next.add(color);
      return next;
    });
  }, []);

  const handleBrandToggle = useCallback((brand: string) => {
    setActiveBrands(prev => {
      const next = new Set(prev);
      next.has(brand) ? next.delete(brand) : next.add(brand);
      return next;
    });
  }, []);

  const handleToggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelected(prev => {
      const allIds = filteredProducts.map(p => p.id);
      const allSelected = allIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        for (const id of allIds) next.delete(id);
        return next;
      }
      return new Set([...prev, ...allIds]);
    });
  }, [filteredProducts]);

  const sectionClass = `${styles.section}${isVisible ? ` ${styles.visible}` : ''}`;

  return (
    <div ref={sectionRef} className={sectionClass}>
      {isVisible && hintVisible && (
        <div className={styles.interactiveHint}>
          <svg className={styles.cursorIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3L5 6.99h3V14h2V6.99h3L9 3zm7 14.01V10h-2v7.01h-3L15 21l4-3.99h-3z"/>
          </svg>
          <span>{t('hero.interactiveHint')}</span>
        </div>
      )}
      <div className={styles.browser} onClick={() => setHintVisible(false)}>
        <div className={styles.chromeBar}>
          <div className={styles.dots}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </div>
          <div className={styles.urlBar}>platform.qbp.solutions/catalogue</div>
        </div>

        <div className={styles.browserBody}>
          <CatalogueFilters
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            activeGrades={activeGrades}
            onGradeToggle={handleGradeToggle}
            years={years}
            activeYears={activeYears}
            onYearToggle={handleYearToggle}
            colors={colors}
            activeColors={activeColors}
            onColorToggle={handleColorToggle}
            brands={brands}
            activeBrands={activeBrands}
            onBrandToggle={handleBrandToggle}
            categoryCounts={categoryCounts}
            prices={categoryPrices}
            priceBounds={priceBounds}
            priceRange={priceRange}
            onPriceChange={setPriceRange}
          />
          <ProductList
            products={filteredProducts}
            selected={selected}
            onToggle={handleToggle}
            onToggleAll={handleToggleAll}
            search={search}
            onSearchChange={setSearch}
            selectedCount={selected.size}
          />
        </div>
      </div>

      <p className={styles.disclaimer}>{t('demo.disclaimer')}</p>

      <div className={styles.scrollIndicator} aria-hidden="true">
        <div className={styles.chevron} />
        <div className={styles.chevron} />
        <div className={styles.chevron} />
      </div>
    </div>
  );
}
